import { useId, useMemo } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { TagDefinitionsState } from '@/hooks/useLinkBuilderDataEntry'
import type { TagEntry } from '@/lib/tagEntry'

interface CustomTagSectionProps {
  state: TagDefinitionsState
  tags: TagEntry[]
  reservedKeys: ReadonlySet<string>
  onAdd: () => void
  onChangeRawKey: (id: string, rawKey: string) => void
  onChangeValue: (id: string, value: string) => void
  onRemove: (id: string) => void
}

export function CustomTagSection({
  state,
  tags,
  reservedKeys,
  onAdd,
  onChangeRawKey,
  onChangeValue,
  onRemove,
}: CustomTagSectionProps) {
  const analyticsKeys = useMemo(() => {
    if (state.status !== 'ready') return new Set<string>()
    return new Set(state.definitions.map((d) => d.queryKey))
  }, [state])

  const myTags = useMemo(
    () => tags.filter((t) => t.rawKey !== undefined && !analyticsKeys.has(t.key)),
    [tags, analyticsKeys],
  )

  const siblingCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const tag of myTags) {
      if (tag.key === '') continue
      counts.set(tag.key, (counts.get(tag.key) ?? 0) + 1)
    }
    return counts
  }, [myTags])

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-medium">Custom</h2>

      {myTags.length > 0 && (
        <div className="space-y-3">
          {myTags.map((tag) => {
            const collidesWithReserved = tag.key !== '' && reservedKeys.has(tag.key)
            const collidesWithSibling = tag.key !== '' && (siblingCounts.get(tag.key) ?? 0) > 1
            return (
              <CustomTagRow
                key={tag.id}
                tag={tag}
                collidesWithReserved={collidesWithReserved}
                collidesWithSibling={collidesWithSibling}
                onChangeRawKey={(rawKey) => onChangeRawKey(tag.id, rawKey)}
                onChangeValue={(value) => onChangeValue(tag.id, value)}
                onRemove={() => onRemove(tag.id)}
              />
            )
          })}
        </div>
      )}

      <Button type="button" variant="outline" onClick={onAdd} className="w-full">
        Add a custom parameter
      </Button>
    </section>
  )
}

interface CustomTagRowProps {
  tag: TagEntry
  collidesWithReserved: boolean
  collidesWithSibling: boolean
  onChangeRawKey: (rawKey: string) => void
  onChangeValue: (value: string) => void
  onRemove: () => void
}

function CustomTagRow({
  tag,
  collidesWithReserved,
  collidesWithSibling,
  onChangeRawKey,
  onChangeValue,
  onRemove,
}: CustomTagRowProps) {
  const keyInputId = useId()
  const valueInputId = useId()
  const errorId = useId()

  const error =
    collidesWithReserved || collidesWithSibling
      ? 'Duplicate parameters are not allowed'
      : null

  const showPreview = tag.key !== ''

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <Label htmlFor={keyInputId}>Name</Label>
        {showPreview && (
          <span className="font-mono text-xs text-muted-foreground">{tag.key}</span>
        )}
      </div>
      <Input
        id={keyInputId}
        spellCheck={false}
        autoComplete="off"
        value={tag.rawKey ?? ''}
        onChange={(e) => onChangeRawKey(e.target.value)}
        aria-invalid={error !== null || undefined}
        aria-describedby={error ? errorId : undefined}
      />
      <Label htmlFor={valueInputId} className="sr-only">
        Value
      </Label>
      <div className="flex gap-2">
        <Input
          id={valueInputId}
          spellCheck={false}
          autoComplete="off"
          placeholder="Value"
          value={tag.value}
          onChange={(e) => onChangeValue(e.target.value)}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Remove custom parameter"
          onClick={onRemove}
        >
          <X className="size-4" aria-hidden />
        </Button>
      </div>
      {error && (
        <p id={errorId} className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  )
}
